import { useState } from "react";
import type { FormEvent } from "react";
import "./Servers.css";
import { Button } from "./Button";
import { Alert, EmptyState } from "./Card";
import { Field, Input } from "./Field";
import { Chip, StatusPill } from "./Status";
import { agoText } from "../chat/api";
import { DownChip } from "./Runner";
import type { AppendSpec, Server, Service } from "../lib/servers";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 서버 › 서비스 — 시안 Main · Add(Design 캔버스 「나루 서비스 화면 시안」) ══
   표 한 줄 안 단추는 없다 — 바로가기는 링크다(새 탭). 상태는 모양 · 글자 · 색이 함께 간다. */

/** 서버 붙음 표식 — 설정 › 러너의 표식과 같은 모양(● 붙음 · ○ 안 붙음). */
export function ServerMark({ s, now }: { s: Server; now?: number }) {
  if (s.connections === 0 && s.downSince) return <DownChip since={s.downSince} now={now} />;
  return s.connections > 0 ? (
    <Chip tone="success">
      <span aria-hidden="true">●</span> {tx("servers.live")}
    </Chip>
  ) : (
    <Chip>
      <span aria-hidden="true">○</span> {tx("servers.down")}
    </Chip>
  );
}

const STATE: Record<Service["state"], { glyph: string; tone: "success" | "warning" | "muted" }> = {
  running: { glyph: "●", tone: "success" },
  stopped: { glyph: "■", tone: "warning" },
  "not-synced": { glyph: "◌", tone: "muted" },
};

function StateChip({ state }: { state: Service["state"] }) {
  const s = STATE[state];
  return (
    <Chip tone={s.tone}>
      <span aria-hidden="true">{s.glyph}</span> {tx(`servers.state.${state === "not-synced" ? "notSynced" : state}`)}
    </Chip>
  );
}

/** 포트 칸 — `KEY=값` 을 그대로 보인다(어느 .env 키인지가 사람이 서버에서 찾을 단서다). */
function Ports({ s }: { s: Service }) {
  if (s.from === "none") return <span className="caption">{tx("servers.port.none")}</span>;
  if (s.ports.length === 0) return <span className="caption">{tx("servers.port.unknown")}</span>;
  return (
    <span className="svc__ports">
      {s.ports.map((p) => (
        <code key={p.key}>
          {p.key}={p.value}
        </code>
      ))}
      {s.from === "list" ? <span className="caption">{tx("servers.port.fromList")}</span> : null}
    </span>
  );
}

function Links({ s }: { s: Service }) {
  if (s.links.length === 0) {
    return <span className="caption">{s.from === "none" ? tx("servers.link.none") : tx("servers.link.unknown")}</span>;
  }
  return (
    <span className="svc__links">
      {s.links.map((l) => (
        <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className={l.kind === "ip" ? "svc__mono" : undefined}>
          {l.label} ↗
        </a>
      ))}
    </span>
  );
}

/** 서비스 표. `services` 가 null 이면 보고가 아직 없다 — 「0개」가 아니라 「모름」이다. */
export function ServiceTable({ server }: { server: Server }) {
  if (server.services === null) {
    return <EmptyState icon="inbox" title={tx("servers.noReport.title")} desc={tx("servers.noReport.desc")} />;
  }
  if (server.services.length === 0) {
    return <EmptyState icon="inbox" title={tx("servers.empty.title")} desc={tx("servers.empty.desc")} />;
  }
  return (
    <div className="b-table-wrap">
      <table className="b-table svc__table" aria-label={tx("servers.table")}>
        <thead>
          <tr>
            <th>{tx("servers.col.state")}</th>
            <th>{tx("servers.col.name")}</th>
            <th>{tx("servers.col.repo")}</th>
            <th>{tx("servers.col.ports")}</th>
            <th>{tx("servers.col.link")}</th>
            <th>{tx("servers.col.dir")}</th>
          </tr>
        </thead>
        <tbody>
          {server.services.map((s) => (
            <tr key={s.name}>
              <td>
                <StateChip state={s.state} />
              </td>
              <td className="is-primary">{s.name}</td>
              <td>
                {s.repo} <span className="caption">· {s.branch}</span>
              </td>
              <td>
                <Ports s={s} />
              </td>
              <td>
                <Links s={s} />
              </td>
              <td className="svc__mono caption">{s.dir}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 「리포 추가」 폼 — 서버에 **아직 없는** 리포만. 이미 클론된 폴더는 서버에서 `make append-existing` 이다
 *  (폴더 경로를 사람이 서버에서 보고 고르는 일이라 웹으로 안 올린다). */
export function AddRepoForm({
  host,
  used,
  suggestions = [],
  taken = [],
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  host: string;
  /** 이 서버에서 쓰는 포트(보고 기준) — 겹치는지 사람이 먼저 보게. 판정은 서버의 `make check` 다. */
  used: string[];
  /** b3x in 에 등록된 리포(`owner/이름`) — 고르기 쉽게. 직접 적어도 된다. */
  suggestions?: string[];
  /** 이 서버에 이미 있는 리포(`owner/이름`, 소문자) — 고르면 콘솔로 보낸다. */
  taken?: string[];
  busy?: boolean;
  error?: string | null;
  onSubmit?: (s: AppendSpec) => void;
  onCancel?: () => void;
}) {
  const [repo, setRepo] = useState("");
  const [branch, setBranch] = useState("dev");
  const [service, setService] = useState("");
  const [port, setPort] = useState("");
  const key = repo.trim().replace(/\.git$/, "").toLowerCase();
  const fullKey = key.includes("/") ? key : key ? `irolab-co/${key}` : "";
  const already = fullKey !== "" && taken.includes(fullKey);
  const repoOk = /^[A-Za-z0-9][A-Za-z0-9._-]*(\/[A-Za-z0-9][A-Za-z0-9._-]*)?$/.test(repo.trim());
  const portOk = port === "" || (/^\d{1,5}$/.test(port) && +port >= 1 && +port <= 65535);
  const name = service.trim() || (repoOk ? repo.trim().split("/").pop()!.replace(/\.git$/, "") : "");
  const cmd = `make append REPO=${repo.trim() || "<리포>"}${branch && branch !== "dev" ? ` BRANCH=${branch}` : ""}${name ? ` S=${name}` : ""}${port ? ` PORT=${port}` : ""}`; // copy-ignore: 명령 견본

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!repoOk || !portOk || already) return;
    onSubmit?.({ host, repo: repo.trim(), branch: branch.trim(), service: service.trim(), port });
  }
  return (
    <section className="b-card svc__add">
      <form className="svc__form" onSubmit={submit}>
        <h3 className="b-card-title">{tx("servers.add.title", { host: `s${host}` })}</h3>
        <p className="caption">{tx("servers.add.lead")}</p>
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Field
          label={tx("servers.add.repo")}
          hint={tx("servers.add.repoHint")}
          error={already ? tx("servers.add.already") : repo && !repoOk ? tx("servers.add.repoBad") : undefined}
        >
          {(id, invalid) => (
            <>
              <Input id={id} invalid={invalid} list="svc-repos" value={repo} placeholder="irolab-co/mops-send" onChange={(e) => setRepo(e.target.value)} autoComplete="off" />
              <datalist id="svc-repos">
                {suggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </>
          )}
        </Field>
        <div className="svc__row">
          <Field label={tx("servers.add.branch")}>{(id) => <Input id={id} value={branch} onChange={(e) => setBranch(e.target.value)} />}</Field>
          <Field label={tx("servers.add.service")} hint={tx("servers.add.serviceHint")}>
            {(id) => <Input id={id} value={service} placeholder={name || undefined} onChange={(e) => setService(e.target.value)} />}
          </Field>
          <Field label={tx("servers.add.port")} hint={tx("servers.add.portHint")} error={portOk ? undefined : tx("servers.add.portBad")}>
            {(id, invalid) => <Input id={id} invalid={invalid} inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.trim())} />}
          </Field>
        </div>
        <p className="caption">{tx("servers.add.used", { ports: used.length ? used.join(" · ") : tx("servers.add.usedNone") })}</p>
        <div className="svc__cmd">
          <span className="caption">{tx("servers.add.cmd")}</span>
          <code>{cmd}</code>
          <span className="caption">{tx("servers.add.how", { host: `s${host}` })}</span>
        </div>
        <div className="svc__actions">
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
              {tx("common.action.cancel")}
            </Button>
          ) : null}
          <Button type="submit" variant="primary" loading={busy} disabled={!repoOk || !portOk || already}>
            {tx("servers.add.submit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** 이 서버의 「리포 추가」 이슈 — 대기 · 도는 중 · 막힘 · 완료가 보드의 표식 그대로 간다. */
export function AppendList({ tasks, now = Date.now() }: { tasks: Issue[]; now?: number }) {
  if (tasks.length === 0) return null;
  return (
    <section className="svc__jobs" aria-label={tx("servers.jobs.title")}>
      <h3>{tx("servers.jobs.title")}</h3>
      <ul>
        {tasks.map((t) => (
          <li key={t.id}>
            <StatusPill status={t.status} />
            <span className="is-primary">{t.ops?.service || t.ops?.repo}</span>
            <span className="caption">{t.ops?.repo} · {agoText(t.updatedAt, now)}</span>
            <a href={`#/board?open=${encodeURIComponent(t.id)}`}>{tx("servers.jobs.open")}</a>
          </li>
        ))}
      </ul>
    </section>
  );
}
